import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase, type User } from '../services/supabase';
import { hasAdminRole } from '../utils/roles';

export interface CommunityGroupSummary {
  id: string;
  name: string;
  is_private?: boolean | null;
}

export interface CommunityGroupMembership {
  role: 'admin' | 'moderator' | 'member';
}

export interface CommunityGroupPost {
  id: string;
  group_id: string;
  content: string;
  created_at: string;
  author_id: string;
  author_name?: string | null;
  status?: string | null;
  images?: string[] | null;
  video_url?: string | null;
  video_id?: string | null;
  edited_at?: string | null;
  original_post?: Record<string, unknown> | null;
  type?: string | null;
  is_pinned?: boolean | null;
  pinned_at?: string | null;
  pinned_by?: string | null;
  likesCount: number;
  commentsCount: number;
  seenCount?: number | null;
  viewerHasSeen?: boolean;
}

export interface CommunityGroupSeenUser {
  userId: string;
  fullName: string;
  avatar?: string | null;
  lastSeenAt: string;
}

export interface CommunityGroupComment {
  id: string;
  post_id: string;
  content: string;
  created_at: string;
  author_id: string;
  author_name?: string | null;
  reply_to_id?: string | null;
  status?: string | null;
  is_pinned?: boolean | null;
  pinned_at?: string | null;
  pinned_by?: string | null;
}

export interface CommunityGroupCommentInput {
  postId: string;
  content: string;
  replyToId?: string | null;
}

export interface CommunityGroupPostEngagement {
  liked: boolean;
}

type GroupRoleSource = string[] | null | undefined;

const normalizeUrlArray = (value: unknown) => {
  if (!Array.isArray(value)) return [] as string[];
  return value
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter((entry): entry is string => entry.length > 0);
};

const resolveAuthorName = (user: User | null, fallback?: string | null) => {
  if (fallback?.trim()) return fallback.trim();

  const userMeta = user?.user_metadata as Record<string, unknown> | undefined;
  const candidates = [
    userMeta?.full_name,
    userMeta?.name,
    userMeta?.display_name,
    user?.email,
    'Community Member',
  ];

  return (
    candidates.find((value): value is string => typeof value === 'string' && value.trim().length > 0) ??
    'Community Member'
  );
};

async function fetchGroupPost(postId: string) {
  const { data, error } = await supabase
    .from('group_posts')
    .select(
      'id, group_id, content, created_at, author_id, author_name, status, images, video_url, video_id, type, is_pinned, pinned_at, pinned_by, edited_at, original_post'
    )
    .eq('id', postId)
    .or('status.is.null,status.eq.published')
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const [likesResult, commentsResult, seenSummaryResult] = await Promise.all([
    supabase.from('group_post_likes').select('id', { count: 'exact', head: true }).eq('post_id', postId),
    supabase
      .from('group_post_comments')
      .select('id', { count: 'exact', head: true })
      .eq('post_id', postId)
      .or('status.is.null,status.eq.published'),
    fetchGroupPostSeenSummaries([postId]),
  ]);

  if (likesResult.error) throw likesResult.error;
  if (commentsResult.error) throw commentsResult.error;

  return {
    id: data.id,
    group_id: data.group_id,
    content: data.content || '',
    created_at: data.created_at,
    author_id: data.author_id,
    author_name: data.author_name || null,
    status: data.status || 'published',
    images: normalizeUrlArray(data.images),
    video_url: data.video_url || null,
    video_id: data.video_id || null,
    edited_at: data.edited_at || null,
    original_post: data.original_post ?? null,
    type: data.type || 'post',
    is_pinned: Boolean(data.is_pinned),
    pinned_at: data.pinned_at ?? null,
    pinned_by: data.pinned_by ?? null,
    likesCount: likesResult.count ?? 0,
    commentsCount: commentsResult.count ?? 0,
    seenCount: seenSummaryResult[postId]?.seenCount ?? null,
    viewerHasSeen: seenSummaryResult[postId]?.viewerHasSeen ?? false,
  } satisfies CommunityGroupPost;
}

export async function fetchPinnedCommunityGroupPosts(groupId: string, limit = 20) {
  if (!groupId) return [] as CommunityGroupPost[];

  const { data, error } = await supabase
    .from('group_posts')
    .select(
      'id, group_id, content, created_at, author_id, author_name, status, images, video_url, video_id, type, is_pinned, pinned_at, pinned_by, edited_at, original_post'
    )
    .eq('group_id', groupId)
    .eq('is_pinned', true)
    .or('status.is.null,status.eq.published')
    .or(`feed_visible_until.is.null,feed_visible_until.gt."${new Date().toISOString()}"`)
    .order('pinned_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false })
    .limit(Math.max(limit, 1));

  if (error) throw error;

  return ((data ?? []) as Array<Record<string, unknown>>).map((post) => ({
    id: String(post.id),
    group_id: String(post.group_id),
    content: typeof post.content === 'string' ? post.content : '',
    created_at: String(post.created_at),
    author_id: String(post.author_id),
    author_name: typeof post.author_name === 'string' ? post.author_name : null,
    status: typeof post.status === 'string' ? post.status : 'published',
    images: normalizeUrlArray(post.images),
    video_url: typeof post.video_url === 'string' ? post.video_url : null,
    video_id: typeof post.video_id === 'string' ? post.video_id : null,
    edited_at: typeof post.edited_at === 'string' ? post.edited_at : null,
    original_post: post.original_post && typeof post.original_post === 'object' ? post.original_post as Record<string, unknown> : null,
    type: typeof post.type === 'string' ? post.type : 'post',
    is_pinned: true,
    pinned_at: typeof post.pinned_at === 'string' ? post.pinned_at : null,
    pinned_by: typeof post.pinned_by === 'string' ? post.pinned_by : null,
    likesCount: 0,
    commentsCount: 0,
  })) satisfies CommunityGroupPost[];
}

async function fetchGroupPostSeenSummaries(postIds: string[]) {
  const uniquePostIds = Array.from(new Set(postIds.filter((postId): postId is string => Boolean(postId))));
  if (uniquePostIds.length === 0) {
    return {} as Record<string, { seenCount: number | null; viewerHasSeen: boolean }>;
  }

  const { data, error } = await supabase.rpc('get_group_post_seen_summaries', {
    p_post_ids: uniquePostIds,
  });

  if (error) throw error;

  return Object.fromEntries(
    ((data ?? []) as Array<Record<string, unknown>>).map((row) => [
      String(row.post_id),
      {
        seenCount:
          row.seen_count === null || typeof row.seen_count === 'undefined'
            ? null
            : Number.isFinite(Number(row.seen_count))
              ? Number(row.seen_count)
              : null,
        viewerHasSeen: Boolean(row.viewer_has_seen),
      },
    ])
  );
}

async function fetchGroupPostSeenList(postId: string) {
  if (!postId) return [] as CommunityGroupSeenUser[];

  const { data, error } = await supabase.rpc('get_group_post_seen_list', {
    p_post_id: postId,
  });

  if (error) throw error;

  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    userId: String(row.user_id),
    fullName:
      typeof row.full_name === 'string' && row.full_name.trim().length > 0 ? row.full_name : 'Community Member',
    avatar: typeof row.avatar === 'string' ? row.avatar : null,
    lastSeenAt: String(row.last_seen_at),
  }));
}

async function recordGroupPostsSeen(postIds: string[]) {
  const uniquePostIds = Array.from(new Set(postIds.filter((postId): postId is string => Boolean(postId))));
  if (uniquePostIds.length === 0) return 0;

  const { data, error } = await supabase.rpc('mark_group_posts_seen', {
    p_post_ids: uniquePostIds,
  });

  if (error) throw error;
  return typeof data === 'number' ? data : Number(data ?? 0);
}

async function fetchGroupComments(postId: string) {
  const { data, error } = await supabase
    .from('group_post_comments')
    .select(
      'id, post_id, content, created_at, author_id, author_name, reply_to_id, status, is_pinned, pinned_at, pinned_by'
    )
    .eq('post_id', postId)
    .or('status.is.null,status.eq.published')
    .order('is_pinned', { ascending: false })
    .order('created_at', { ascending: true });

  if (error) throw error;

  return ((data ?? []) as Array<Record<string, unknown>>)
    .map((comment) => ({
      id: String(comment.id),
      post_id: String(comment.post_id),
      content: typeof comment.content === 'string' ? comment.content : '',
      created_at: String(comment.created_at),
      author_id: String(comment.author_id),
      author_name: typeof comment.author_name === 'string' ? comment.author_name : 'Community Member',
      reply_to_id: typeof comment.reply_to_id === 'string' ? comment.reply_to_id : null,
      status: typeof comment.status === 'string' ? comment.status : 'published',
      is_pinned: Boolean(comment.is_pinned),
      pinned_at: typeof comment.pinned_at === 'string' ? comment.pinned_at : null,
      pinned_by: typeof comment.pinned_by === 'string' ? comment.pinned_by : null,
    }))
    .sort((a, b) => {
      if (Boolean(a.is_pinned) !== Boolean(b.is_pinned)) {
        return a.is_pinned ? -1 : 1;
      }
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }) as CommunityGroupComment[];
}

async function setGroupPostPinned(postId: string, pinned: boolean) {
  const { data, error } = await supabase.rpc('set_group_post_pinned', {
    p_post_id: postId,
    p_pinned: pinned,
  });
  if (error) throw error;
  return {
    id: String((data as { id?: string } | null)?.id ?? postId),
    is_pinned: Boolean((data as { is_pinned?: boolean } | null)?.is_pinned ?? pinned),
  };
}

async function setGroupPostCommentPinned(commentId: string, pinned: boolean) {
  const { data, error } = await supabase.rpc('set_group_post_comment_pinned', {
    p_comment_id: commentId,
    p_pinned: pinned,
  });
  if (error) throw error;
  return {
    id: String((data as { id?: string } | null)?.id ?? commentId),
    post_id: String((data as { post_id?: string } | null)?.post_id ?? ''),
    is_pinned: Boolean((data as { is_pinned?: boolean } | null)?.is_pinned ?? pinned),
  };
}

async function fetchGroupEngagement(userId: string, postId: string) {
  if (!userId || !postId) {
    return { liked: false } satisfies CommunityGroupPostEngagement;
  }

  const { data, error } = await supabase
    .from('group_post_likes')
    .select('post_id')
    .eq('post_id', postId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  return { liked: Boolean(data) } satisfies CommunityGroupPostEngagement;
}

async function fetchGroupSummary(groupId: string) {
  const { data, error } = await supabase.from('groups').select('id, name, is_private').eq('id', groupId).single();
  if (error) throw error;
  return data as CommunityGroupSummary;
}

async function fetchGroupMembership(groupId: string, userId: string) {
  if (!groupId || !userId) return null;

  const { data, error } = await supabase
    .from('group_members')
    .select('role')
    .eq('group_id', groupId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  return (data as CommunityGroupMembership | null) ?? null;
}

export const communityGroupPostQueryKeys = {
  root: ['community-group-posts'] as const,
  group: (groupId?: string) => ['community-group-posts', 'group', groupId ?? 'unknown'] as const,
  membership: (groupId?: string, userId?: string) =>
    ['community-group-posts', 'membership', groupId ?? 'unknown', userId ?? 'anonymous'] as const,
  pinned: (groupId?: string) => ['community-group-posts', 'pinned', groupId ?? 'unknown'] as const,
  post: (postId?: string) => ['community-group-posts', 'post', postId ?? 'unknown'] as const,
  comments: (postId?: string) => ['community-group-posts', 'comments', postId ?? 'unknown'] as const,
  engagement: (userId?: string, postId?: string) =>
    ['community-group-posts', 'engagement', userId ?? 'anonymous', postId ?? 'unknown'] as const,
  seenList: (postId?: string) => ['community-group-posts', 'seen-list', postId ?? 'unknown'] as const,
};

export function canPinGroupContent(membership?: CommunityGroupMembership | null) {
  return membership?.role === 'admin' || membership?.role === 'moderator';
}

export function useCommunityGroupAccess(groupId?: string, userId?: string, roles?: GroupRoleSource) {
  const canManageGroup = hasAdminRole(roles);
  const groupQuery = useQuery<CommunityGroupSummary>({
    queryKey: communityGroupPostQueryKeys.group(groupId),
    queryFn: () => fetchGroupSummary(groupId ?? ''),
    enabled: Boolean(groupId),
  });

  const membershipQuery = useQuery<CommunityGroupMembership | null>({
    queryKey: communityGroupPostQueryKeys.membership(groupId, userId),
    queryFn: () => fetchGroupMembership(groupId ?? '', userId ?? ''),
    enabled: Boolean(groupId && userId),
  });

  const isMember = Boolean(membershipQuery.data);
  const canViewGroupContent =
    Boolean(groupQuery.data) && (!groupQuery.data?.is_private || isMember || canManageGroup);
  const canCreateGroupContent = isMember || canManageGroup;
  const canPinContent = canPinGroupContent(membershipQuery.data);

  return {
    group: groupQuery.data ?? null,
    membership: membershipQuery.data ?? null,
    canManageGroup,
    canPinContent,
    canViewSeenStatus: membershipQuery.data?.role === 'admin',
    canViewGroupContent,
    canCreateGroupContent,
    isLoading: groupQuery.isLoading || membershipQuery.isLoading,
    error: groupQuery.error ?? membershipQuery.error ?? null,
  };
}

export function useCommunityGroupPost(postId?: string) {
  return useQuery<CommunityGroupPost | null>({
    queryKey: communityGroupPostQueryKeys.post(postId),
    queryFn: () => fetchGroupPost(postId ?? ''),
    enabled: Boolean(postId),
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });
}

export function useCommunityGroupPostComments(postId?: string) {
  return useQuery<CommunityGroupComment[]>({
    queryKey: communityGroupPostQueryKeys.comments(postId),
    queryFn: () => fetchGroupComments(postId ?? ''),
    enabled: Boolean(postId),
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
  });
}

export function useCommunityGroupPostEngagement(userId?: string, postId?: string) {
  return useQuery<CommunityGroupPostEngagement>({
    queryKey: communityGroupPostQueryKeys.engagement(userId, postId),
    queryFn: () => fetchGroupEngagement(userId ?? '', postId ?? ''),
    enabled: Boolean(userId && postId),
    initialData: { liked: false },
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });
}

export function useCommunityGroupPostSeenList(postId?: string, enabled: boolean = true) {
  return useQuery<CommunityGroupSeenUser[]>({
    queryKey: communityGroupPostQueryKeys.seenList(postId),
    queryFn: () => fetchGroupPostSeenList(postId ?? ''),
    enabled: enabled && Boolean(postId),
    staleTime: 30_000,
  });
}

export function useRecordCommunityGroupPostsSeen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postIds: string[]) => recordGroupPostsSeen(postIds),
    onSuccess: async (_updatedCount, postIds) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.root }),
        ...Array.from(new Set(postIds.filter((postId): postId is string => Boolean(postId)))).map((postId) =>
          queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.seenList(postId) })
        ),
      ]);
    },
  });
}

export function useCommunityGroupPostRealtime(options?: { enabled?: boolean; postId?: string }) {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;
  const postId = options?.postId;

  useEffect(() => {
    if (!enabled) return undefined;

    const invalidateGroupPost = () => {
      void queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.root });
      if (postId) {
        void queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.post(postId) });
        void queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.comments(postId) });
        void queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.engagement(undefined, postId) });
      }
    };

    const channel = supabase
      .channel(`community-group-post-live-${postId ?? 'feed'}-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'group_posts',
          ...(postId ? { filter: `id=eq.${postId}` } : {}),
        },
        invalidateGroupPost
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'group_post_comments',
          ...(postId ? { filter: `post_id=eq.${postId}` } : {}),
        },
        invalidateGroupPost
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'group_post_likes',
          ...(postId ? { filter: `post_id=eq.${postId}` } : {}),
        },
        invalidateGroupPost
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [enabled, postId, queryClient]);
}

export function useToggleCommunityGroupPostLike(userId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (postId: string) => {
      if (!userId) throw new Error('You must be signed in to like a post.');

      const { data: existingLike, error: existingError } = await supabase
        .from('group_post_likes')
        .select('post_id')
        .eq('post_id', postId)
        .eq('user_id', userId)
        .maybeSingle();

      if (existingError) throw existingError;

      if (existingLike) {
        const { error } = await supabase
          .from('group_post_likes')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', userId);
        if (error) throw error;
        return { postId, liked: false };
      }

      const { error } = await supabase.from('group_post_likes').insert({
        post_id: postId,
        user_id: userId,
        created_at: new Date().toISOString(),
      });
      if (error) throw error;
      return { postId, liked: true };
    },
    onSuccess: async (_result, postId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.root }),
        queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.post(postId) }),
      ]);
    },
  });
}

export function useCreateCommunityGroupComment(user: User | null, authorName?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CommunityGroupCommentInput) => {
      if (!user?.id) throw new Error('You must be signed in to comment.');

      const trimmedContent = input.content.trim();
      if (!trimmedContent) throw new Error('Comment content is required.');

      const payload = {
        post_id: input.postId,
        content: trimmedContent,
        author_id: user.id,
        author_name: resolveAuthorName(user, authorName),
        reply_to_id: input.replyToId ?? null,
        status: 'published',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase.from('group_post_comments').insert(payload).select().single();
      if (error) throw error;
      return data as CommunityGroupComment;
    },
    onSuccess: async (createdComment) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.root }),
        queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.comments(createdComment.post_id) }),
        queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.post(createdComment.post_id) }),
      ]);
    },
  });
}

export function useSetCommunityGroupPostPinned(groupId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, pinned }: { postId: string; pinned: boolean }) =>
      setGroupPostPinned(postId, pinned),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.root }),
        queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.post(result.id) }),
        groupId
          ? queryClient.invalidateQueries({ queryKey: ['community-group-feed', groupId] })
          : Promise.resolve(),
        groupId
          ? queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.pinned(groupId) })
          : Promise.resolve(),
      ]);
    },
  });
}

export function useSetCommunityGroupPostCommentPinned() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, pinned }: { commentId: string; pinned: boolean }) =>
      setGroupPostCommentPinned(commentId, pinned),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: communityGroupPostQueryKeys.root }),
        result.post_id
          ? queryClient.invalidateQueries({
              queryKey: communityGroupPostQueryKeys.comments(result.post_id),
            })
          : Promise.resolve(),
        result.post_id
          ? queryClient.invalidateQueries({ queryKey: ['community-group-post-comments', result.post_id] })
          : Promise.resolve(),
      ]);
    },
  });
}